// pages/WebsiteLinks/ScrapeStatusModal.js
import React, { useState } from 'react';
import Modal from 'react-modal';
import { Tooltip } from "@mui/material";
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// --- Helper Component ---
function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(
          props.value,
        )}%`}</Typography>
      </Box>
    </Box>
  );
}
// --- End Helper Component ---

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        width: '60%',
        maxWidth: '800px',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.75)'
    }
};

const minimizedStyles = {
    content: {
        width: '300px',
        height: '50px',
        top: 'auto',
        left: 'auto',
        right: '20px',
        bottom: '20px',
        marginRight: '0',
        transform: 'none',
        maxWidth: 'none',
        maxHeight: 'none',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.0)'
    }
};

if (typeof window !== 'undefined') {
    const rootElement = document.getElementById('root');
    if (rootElement) {
        Modal.setAppElement('#root');
    } else {
        console.warn('Modal.setAppElement: #root element not found.');
    }
}

// <<< FIX: Provide a default empty array for scrapeResults to prevent crashes
function ScrapeStatusModal({ 
    isOpen, 
    onRequestClose, 
    scrapeResults = [], 
    modalTitle = "Scraping Status" 
}) {
    const [isMinimized, setIsMinimized] = useState(false);

    const toggleMinimize = () => {
        setIsMinimized(!isMinimized);
    };

    const handleClose = () => {
        onRequestClose(); 
    };
    
    const handleMinimizeOrClose = () => {
        if (!isMinimized) {
            toggleMinimize();
        }
    };

    const handleMaximize = () => {
        setIsMinimized(false);
    };

    const totalTasks = scrapeResults.length;
    const completedTasks = scrapeResults.filter(
        result => result.status === 'success' || result.status === 'error'
    ).length;
    const progressValue = totalTasks === 0 ? 0 : (completedTasks / totalTasks) * 100;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            style={isMinimized ? minimizedStyles : customStyles}
            contentLabel={modalTitle}
            shouldCloseOnOverlayClick={!isMinimized}
        >
            {isMinimized ? (
                <>
                    <span style={{ fontSize: '16px', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexGrow: 1, marginRight: '10px' }}>{modalTitle}</span>
                    <button className="btn btn-sm btn-primary" onClick={handleMaximize} title="Maximize">
                        <i className="fa fa-window-maximize" aria-hidden="true"></i>
                    </button>
                </>
            ) : (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h2 style={{ flexShrink: 0, marginBottom: '0' }}>{modalTitle}</h2>
                        <button className="btn btn-sm btn-secondary" onClick={handleMinimizeOrClose} title="Minimize">
                            <i className="fa fa-window-minimize" aria-hidden="true"></i>
                        </button>
                    </div>

                    <Box sx={{ width: '100%', flexShrink: 0 }}>
                         <LinearProgressWithLabel value={progressValue} />
                    </Box>
                    
                    <div style={{
                        overflowY: 'auto',
                        maxHeight: 'calc(70vh - 120px)',
                        flexGrow: 1,
                        marginBottom: '1rem'
                    }}>
                        <ul style={{ listStyleType: 'none', paddingLeft: 0, margin: 0 }}>
                            {scrapeResults.map((result, index) => (
                                <li key={index} style={{ 
                                    marginBottom: '12px', 
                                    paddingBottom: '8px',
                                    borderBottom: '1px solid #eee',
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'flex-start'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: '4px'}}>
                                        <span style={{ minWidth: '30px', fontWeight: 'bold' }}>{`${index + 1})`}</span>
                                        <span style={{ wordBreak: 'break-all', marginRight: '10px', flexGrow: 1 }}>{`${result.website_link} `}</span>
                                        {result.status === 'pending' && (
                                            <span className="badge bg-secondary ms-auto">Pending...</span>
                                        )}
                                        {result.status === 'success' && (
                                            <span className="badge bg-success ms-auto">Success</span>
                                        )}
                                        {result.status === 'error' && (
                                            <span className="badge bg-danger ms-auto">Error</span>
                                        )}
                                    </div>
                                    {result.message && (
                                        <div style={{ 
                                            fontSize: '0.85em', 
                                            color: result.status === 'error' ? '#dc3545' : '#28a745',
                                            paddingLeft: '30px',
                                            wordBreak: 'break-word'
                                        }}>
                                            {result.message}
                                        </div>
                                    )}
                                    {!result.message && result.error && result.status === 'error' && (
                                        <div style={{ paddingLeft: '30px' }}>
                                            <Tooltip title={result.error} placement="right">
                                                <i className="fa fa-exclamation-circle text-danger" aria-hidden="true" style={{ cursor: 'help', fontSize: '0.85em' }}>
                                                    <span style={{marginLeft: '5px', color: '#6c757d'}}>Details</span>
                                                </i>
                                            </Tooltip>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div style={{ flexShrink: 0, textAlign: 'right', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                        <button className="btn btn-secondary" onClick={handleClose}>Close</button>
                    </div>
                </>
            )}
        </Modal>
    );
}

export default ScrapeStatusModal;